import { ChangeEvent, useMemo } from "react";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { SelectOption } from "@idw-types/common";
import {
    FormattedIdentityProviderDetails,
    IdentityProviderCategory,
    IdentityProviderCompany,
    IdentityProviderProtocol
} from "@idw-types/data/identity-providers";
import { IdentityProviderGeneralInfoValidationErrors } from "@idw-types/common/validation/identity-provider/identity-provider-validation-errors";
import { mapEntitiesWithNameAndIdToSelectOptions } from "@utils/select-option-mapping-util";
import { sortStrings } from "@utils/string-util";
import { InputFormGroup } from "../../common/input-form-group/input-form-group";
import { SelectFormGroup } from "../../common/select-form-group/select-form-group";
import { ContentPageHeader } from "../../common/content-page-header/content-page-header";
import { ContentPageBody } from "../../common/content-page-body/content-page-body";
import styles from "../identity-provider-shared-style.less";

type IdentityProviderInfoContainerProps = {
    isDisabled: boolean;
    labelWidth: LabelWidthTypes;
    categories: Array<IdentityProviderCategory>;
    companies: Array<IdentityProviderCompany>;
    protocols: Array<IdentityProviderProtocol>;
    validationErrors: IdentityProviderGeneralInfoValidationErrors
    state: FormattedIdentityProviderDetails;
    setState: (state: FormattedIdentityProviderDetails) => void;
};

export const IdentityProviderInfoContainer = (props: IdentityProviderInfoContainerProps) => {
    const {
        isDisabled,
        labelWidth,
        categories,
        companies,
        protocols,
        validationErrors,
        state,
        setState
    } = props;

    const handleProtocolChange = (newValue: SelectOption) => {
        setState({
            ...state,
            protocol: protocols.find(protocol => protocol.key === newValue.value)
        });
    };

    const handleCategoryChange = (newValue: SelectOption) => {
        setState({
            ...state,
            category: categories.find(category => category.key === newValue.value),
        });
    };

    const handleCompanyChange = (newValue: SelectOption) => {
        setState({
            ...state,
            company: companies.find(company => company.id === newValue.value)
        });
    };

    const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        const spaceRegex = /\s/g;
        const newCode = (event.target.value || "").replace(spaceRegex, "-").toLowerCase();
        setState({
            ...state,
            name: event.target.value,
            code: newCode
        });
    };

    const handleCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            code: event.target.value
        });
    };

    const handleRedirectUrlChange = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            companyIDPInfo: {
                ...state.companyIDPInfo,
                idpRedirectURL: event.target.value
            }
        });
    };

    const protocolOptions = useMemo(() => protocols.map(protocol => ({
        label: protocol.value,
        value: protocol.key
    })), [protocols]);

    const selectedProtocolOption = useMemo(() => {
        if (state.protocol) {
            return { label: state.protocol.value, value: state.protocol.key };
        }
        return null;
    }, [state.protocol]);

    const categoryOptions = useMemo(() => categories.map(category => ({
        label: category.value,
        value: category.key
    })), [categories]);

    const selectedCategoryOption = useMemo(() => {
        if (state.category) {
            return { label: state.category.value, value: state.category.key };
        }
        return null;
    }, [state.category]);

    const companyOptions = useMemo(() => {
        return mapEntitiesWithNameAndIdToSelectOptions(companies).sort((a, b) => sortStrings(a.label, b.label))
    }, [companies]);

    const selectedCompanyOption = useMemo(() => {
        if (state.company) {
            return { label: state.company.name, value: state.company.id };
        }
        return null;
    }, [state.company]);

    const resource = resources.pages.identityProviderDetails.tabs.generalInfo;

    return (
        <div className={styles.identityProviderDetailsSection}>
            <ContentPageHeader title={resource.header} />
            <ContentPageBody isMaxWidth={true}>
                <span className={styles.description}>

                </span>
                <SelectFormGroup
                    controlId="identity-provider-protocol-select"
                    automationId="identity-provider-protocol-select"
                    ariaLabel={resource.inputs.protocol.label}
                    label={resource.inputs.protocol.label}
                    labelWidth={labelWidth}
                    disabled={isDisabled}
                    isClearable={false}
                    isRequired={true}
                    isSearchable={false}
                    onChange={handleProtocolChange}
                    options={protocolOptions}
                    selectedOption={selectedProtocolOption}
                />
                <InputFormGroup
                    controlId="identity-provider-name-input"
                    automationId="identity-provider-name-input"
                    label={resource.inputs.identityProviderName.label}
                    ariaLabel={resource.inputs.identityProviderName.label}
                    value={state.name || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    onChange={handleNameChange}
                    labelWidth={labelWidth}
                />
                {validationErrors.nameError}
                <InputFormGroup
                    controlId="identity-provider-code-input"
                    automationId="identity-provider-code-input"
                    label={resource.inputs.code.label}
                    ariaLabel={resource.inputs.code.label}
                    value={state.code || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    onChange={handleCodeChange}
                    labelWidth={labelWidth}
                />
                {validationErrors.codeError}
                <SelectFormGroup
                    controlId="identity-provider-category-select"
                    automationId="identity-provider-category-select"
                    ariaLabel={resource.inputs.category.label}
                    label={resource.inputs.category.label}
                    labelWidth={labelWidth}
                    disabled={isDisabled}
                    isClearable={false}
                    isRequired={true}
                    isSearchable={false}
                    onChange={handleCategoryChange}
                    options={categoryOptions}
                    selectedOption={selectedCategoryOption}
                />
                {validationErrors.categoryError}
                <SelectFormGroup
                    controlId="identity-provider-organization-select"
                    automationId="identity-provider-organization-select"
                    ariaLabel={resource.inputs.organization.label}
                    label={resource.inputs.organization.label}
                    labelWidth={labelWidth}
                    disabled={isDisabled}
                    isClearable={false}
                    isRequired={true}
                    isSearchable={true}
                    onChange={handleCompanyChange}
                    options={companyOptions}
                    selectedOption={selectedCompanyOption}
                />
                {validationErrors.organizationError}
                <InputFormGroup
                    controlId="identity-provider-redirect-url-input"
                    automationId="identity-provider-redirect-url-input"
                    label={resource.inputs.redirectUrl.label}
                    ariaLabel={resource.inputs.redirectUrl.label}
                    value={state.companyIDPInfo.idpRedirectURL || ""}
                    disabled={isDisabled}
                    isRequired={false}
                    onChange={handleRedirectUrlChange}
                    labelWidth={labelWidth}
                />
            </ContentPageBody>
        </div>
    );
}